import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import gsap from "gsap";
import { CLEANING, SERVICES, SITE, contactPath } from "../data/site";
import Seo, { localBusinessJsonLd } from "../Components/Seo";
import Picture from "../Components/Picture";
import Reveal from "../Components/Reveal";

export default function Cleaning() {
  const heroRef = useRef(null);
  const [slide, setSlide] = useState(0);
  const slides = CLEANING.heroSlides;

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;
    const ctx = gsap.context(() => {
      gsap.from(".hero-anim", {
        y: 36,
        opacity: 0,
        duration: 1.05,
        stagger: 0.1,
        ease: "power3.out",
        delay: 0.08,
      });
    }, heroRef);
    return () => ctx.revert();
  }, []);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced || slides.length < 2) return;
    let id;
    const start = () => {
      id = window.setInterval(() => {
        setSlide((s) => (s + 1) % slides.length);
      }, 9000);
    };
    const stop = () => window.clearInterval(id);
    const onVis = () => {
      stop();
      if (!document.hidden) start();
    };
    start();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [slides.length]);

  const jsonLd = [
    localBusinessJsonLd(),
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Gebäudereinigung",
      provider: { "@type": "HomeAndConstructionBusiness", name: SITE.name },
      description: CLEANING.description,
      areaServed: SITE.area,
    },
  ];

  return (
    <>
      <Seo
        title="Gebäudereinigung in Mülheim & Essen"
        description={CLEANING.description}
        path="/reinigung"
        image="/images/cleaning-service-1.jpg"
        jsonLd={jsonLd}
      />

      <section className="hero is-page" ref={heroRef}>
        <div className="hero-media">
          {slides.map((img, i) => (
            <div
              key={img.src}
              className={`hero-slide${i === slide ? " is-on" : ""}`}
              aria-hidden={i !== slide}
            >
              <Picture
                src={img.src}
                fallback={img.fallback}
                alt={i === slide ? img.alt : ""}
                width={1631}
                height={900}
                loading={i === 0 ? "eager" : "lazy"}
                fetchPriority={i === 0 ? "high" : undefined}
              />
            </div>
          ))}
        </div>
        <div className="hero-content">
          <p className="kicker hero-anim" style={{ color: "var(--cyan)" }}>
            Leistung {CLEANING.number} · Mülheim · Essen
          </p>
          <h1 className="display hero-anim">
            Reinigung,
            <br />
            <em>die man merkt.</em>
          </h1>
          <p className="hero-lead hero-anim">{CLEANING.lead}</p>
          <div className="hero-actions hero-anim">
            <Link className="btn btn-primary" to={contactPath("reinigung")}>
              Angebot anfragen
            </Link>
            <a className="btn btn-ghost" href={`tel:${SITE.phoneTel}`}>
              {SITE.phoneDisplay}
            </a>
          </div>
          <div className="hero-meta hero-anim">
            {CLEANING.qualities.map((item) => (
              <div key={item.title}>
                <strong>{item.title}</strong>
                {item.text}
              </div>
            ))}
          </div>
        </div>
        <p className="hero-caption">{slides[slide].caption}</p>
      </section>

      <section className="section intro">
        <div className="wrap intro-grid">
          <Reveal className="intro-copy">
            <p className="kicker">Qualität</p>
            <h2 className="display">So oft und so gründlich, wie Sie es brauchen.</h2>
            <p className="intro-lead">
              CCS Gebäudemanagement ist Ihr Partner für Hausmeister- und
              Reinigungsdienste. Ob Unterhalt, Glas oder der Ernstfall nach einem
              Brand: ein Ansprechpartner, ein Festpreis, ein sauberes Objekt.
            </p>
          </Reveal>
          <Reveal delay={120} className="intro-visual">
            <Picture
              src="/images/opt/cleaning-service-2.webp"
              fallback="/images/cleaning-service-2.jpg"
              alt="Streifenfreie Fensterreinigung innen und außen"
              width={1200}
              height={800}
            />
            <p className="intro-caption">Glas — streifenfrei</p>
          </Reveal>
          <Reveal delay={80} className="intro-aside">
            <ul className="intro-points">
              {CLEANING.qualities.map((item) => (
                <li key={item.n}>
                  <span>{item.n}</span>
                  <div>
                    <strong>{item.title}</strong>
                    <p>{item.text}</p>
                  </div>
                </li>
              ))}
            </ul>
            <a className="intro-more" href="#angebote">
              Zu den Angeboten →
            </a>
          </Reveal>
        </div>
      </section>

      <section className="section" id="angebote" style={{ paddingTop: 0 }}>
        <div className="wrap">
          <Reveal>
            <p className="kicker">Angebote</p>
            <h2 className="display clean-head">Drei Wege zu einem gepflegten Objekt.</h2>
          </Reveal>
          <div className="clean-offer">
            {CLEANING.services.map((item, i) => (
              <article className="clean-block" id={item.id} key={item.id}>
                <Reveal className="clean-block-media" delay={40}>
                  <Picture
                    src={item.image}
                    fallback={item.imageFallback}
                    alt={item.title}
                    width={1200}
                    height={800}
                  />
                </Reveal>
                <Reveal className="clean-block-copy" delay={80}>
                  <p className="kicker">
                    {String(i + 1).padStart(2, "0")} · {item.kicker}
                  </p>
                  <h3 className="display">{item.title}</h3>
                  <p className="muted">{item.text}</p>
                  {item.extra ? (
                    <ul className="check-list">
                      {item.extra.map((x) => (
                        <li key={x}>
                          <span aria-hidden="true">▸</span>
                          {x}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  <div className="hero-actions">
                    <Link className="btn btn-navy" to={contactPath("reinigung")}>
                      {item.title} anfragen
                    </Link>
                  </div>
                </Reveal>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section clean-why">
        <div className="wrap">
          <Reveal>
            <p className="kicker" style={{ color: "var(--cyan)" }}>
              Warum professionell
            </p>
            <h2 className="display">Fünf Gründe, die man im Objekt spürt.</h2>
          </Reveal>
          <div className="clean-why-grid">
            {CLEANING.benefits.map((item, i) => (
              <Reveal className="clean-why-item" key={item.title} delay={i * 70}>
                <span className="clean-why-n">{String(i + 1).padStart(2, "0")}</span>
                <strong>{item.title}</strong>
                <p>{item.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="wrap">
          <Reveal>
            <p className="kicker">Einsatzgebiet</p>
            <h2 className="display" style={{ fontSize: "clamp(2rem, 5vw, 3.2rem)" }}>
              Nah am Objekt. Schnell vor Ort.
            </h2>
            <p className="muted" style={{ marginTop: "0.75rem", maxWidth: "36rem" }}>
              Sitz in Mülheim an der Ruhr – im Einsatz im gesamten westlichen Ruhrgebiet.
            </p>
            <div className="area-list">
              {SITE.area.map((city) => (
                <span className="chip" key={city}>
                  {city}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 0 }}>
        <div className="wrap">
          <p className="kicker">Weitere Leistungen</p>
          <div className="service-list" style={{ marginTop: "1rem" }}>
            {SERVICES.map((s) => (
              <Link key={s.slug} to={`/leistungen/${s.slug}`} className="service-row">
                <span className="service-num">{s.number}</span>
                <span className="service-title">{s.title}</span>
                <span className="service-go">Ansehen →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="section cta-band">
        <div className="wrap">
          <p className="kicker" style={{ color: "var(--cyan)", justifyContent: "center" }}>
            Nächster Schritt
          </p>
          <h2 className="display">Bereit für ein sauberes Objekt?</h2>
          <p>Schildern Sie kurz den Bedarf. Wir melden uns mit einem klaren Festpreis.</p>
          <div className="cta-actions">
            <Link className="btn btn-primary" to={contactPath("reinigung")}>
              Anfrage starten
            </Link>
            <a className="btn btn-ghost" href={`tel:${SITE.phoneTel}`}>
              {SITE.phoneDisplay}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
